import type {LinkProps} from '@mui/material/Link';
import Link from '@mui/material/Link';
import {styled} from '@mui/material/styles';
import {mergeClasses} from 'minimal-shared/utils';
import {forwardRef} from 'react';
import {Link as RouterLink} from 'react-router';

import {logoClasses} from './classes';

// ----------------------------------------------------------------------

export type LogoProps = LinkProps & {
  isSingle?: boolean;
  disabled?: boolean;
};

export const Logo = forwardRef<HTMLAnchorElement, LogoProps>((props, ref) => {
  const {className, href = '/', isSingle = true, disabled, sx, ...other} = props;

  const singleLogo = (
    <svg width="100%" height="100%" viewBox="0 0 120 120" fill="none" xmlns="http://www.w3.org/2000/svg">
      <g clipPath="url(#clip0_1386_1709)">
        <path
          d="M0 26.6667C0 11.9391 11.9391 0 26.6667 0H93.3333C108.061 0 120 11.9391 120 26.6667V93.3333C120 108.061 108.061 120 93.3333 120H26.6667C11.9391 120 0 108.061 0 93.3333V26.6667Z"
          fill="#155FEE"
        />
        <g opacity="0.6">
          <path
            d="M35 36.6667H30V51.6667H46.6667V93.3334H63.3333V88.3334H61.6667V91.6668H48.3333V50.0001H31.6667V38.3334H35M73.3333 51.6667H80V46.6667H78.3333V50.0001H73.3333"
            fill="white"
          />
        </g>
        <g opacity="0.8">
          <path
            d="M68.3333 83.3334H66.65V86.6668H60H53.3333V45.0001H36.6667V33.3334H40V31.6667H35V46.6667H51.6667V88.3334H68.3333V83.3334ZM73.3333 46.6667H85V41.6667H83.3333V45.0001H73.3333V46.6667Z"
            fill="white"
          />
        </g>
        <path d="M90 41.6667V26.6667H40V41.6667H56.6667V83.3334H73.3333V41.6667H90Z" fill="white" />
      </g>
      <defs>
        <clipPath id="clip0_1386_1709">
          <rect width="120" height="120" fill="white" />
        </clipPath>
      </defs>
    </svg>
  );

  const fullLogo = (
    <svg width="100%" height="100%" viewBox="0 0 300 107" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M299.216 55.0233H285.278V76.3176C285.278 81.1572 285.472 81.3508 289.924 81.3508H299.216V93.7402H284.891C274.631 93.7402 269.791 90.3524 269.791 78.0598V55.0233H260.499V42.6339H269.791V28.6958H285.278V42.6339H299.216V55.0233Z"
        fill="#155FEE"
      />
      <path
        d="M234.232 41.0852C251.073 41.0852 261.333 53.281 261.333 68.187C261.333 83.093 251.073 95.2888 234.232 95.2888C217.39 95.2888 207.13 83.093 207.13 68.187C207.13 53.281 217.39 41.0852 234.232 41.0852ZM234.232 82.1251C241.878 82.1251 245.847 75.7368 245.847 68.187C245.847 60.6372 241.878 54.2489 234.232 54.2489C226.585 54.2489 222.617 60.6372 222.617 68.187C222.617 75.7368 226.585 82.1251 234.232 82.1251Z"
        fill="#155FEE"
      />
      <path
        d="M154.48 75.9304H169.773C169.87 79.8989 172.193 83.6738 179.549 83.6738C186.228 83.6738 188.551 81.6411 188.551 79.0278C188.551 75.9304 183.905 75.0593 175.29 74.0914C157.481 72.0587 156.029 63.057 156.029 57.7335C156.029 49.119 164.159 41.0852 179.452 41.0852C191.745 41.0852 201.715 46.6992 202.489 58.895H187.196C187.002 55.1201 184.776 52.7003 179.162 52.7003C173.645 52.7003 171.516 54.5393 171.516 57.0559C171.516 58.7982 172.58 60.4437 181.001 61.218C195.133 62.5731 204.038 67.0255 204.038 78.0598C204.038 87.8358 194.746 95.2888 179.162 95.2888C163.482 95.2888 155.158 87.9326 154.48 75.9304Z"
        fill="#155FEE"
      />
      <path
        d="M101.83 75.9304H117.124C117.22 79.8989 119.543 83.6738 126.9 83.6738C133.578 83.6738 135.901 81.6411 135.901 79.0278C135.901 75.9304 131.255 75.0593 122.641 74.0914C104.831 72.0587 103.379 63.057 103.379 57.7335C103.379 49.119 111.51 41.0852 126.803 41.0852C139.095 41.0852 149.065 46.6992 149.839 58.895H134.546C134.353 55.1201 132.126 52.7003 126.512 52.7003C120.995 52.7003 118.866 54.5393 118.866 57.0559C118.866 58.7982 119.931 60.4437 128.352 61.218C142.483 62.5731 151.388 67.0255 151.388 78.0598C151.388 87.8358 142.096 95.2888 126.512 95.2888C110.832 95.2888 102.508 87.9326 101.83 75.9304Z"
        fill="#155FEE"
      />
      <path
        d="M80.9188 36.4392V24.0498H96.4055V36.4392H80.9188ZM80.9188 93.7402V42.6339H96.4055V93.7402H80.9188Z"
        fill="#155FEE"
      />
      <path
        d="M233.308 22.0479H239.5C239.54 23.6549 240.48 25.1835 243.459 25.1835C246.163 25.1835 247.104 24.3604 247.104 23.3021C247.104 22.0479 245.223 21.6951 241.734 21.3032C234.523 20.4801 233.935 16.835 233.935 14.6793C233.935 11.1909 237.227 7.93774 243.42 7.93774C248.398 7.93774 252.435 10.211 252.748 15.1496H246.555C246.477 13.621 245.576 12.6411 243.302 12.6411C241.068 12.6411 240.206 13.3858 240.206 14.4049C240.206 15.1104 240.637 15.7767 244.047 16.0903C249.769 16.639 253.375 18.442 253.375 22.9102C253.375 26.8688 249.613 29.8868 243.302 29.8868C236.953 29.8868 233.582 26.908 233.308 22.0479Z"
        fill="#155FEE"
      />
      <path
        d="M223.964 7.93774C228.432 7.93774 231.411 10.8382 231.411 16.7174V29.2597H225.139V19.2258C225.139 14.5617 224.081 13.2682 221.847 13.2682C219.299 13.2682 216.987 16.5214 216.987 21.3032V29.2597H210.716V8.56486H216.987V9.54473C216.987 10.4854 216.791 11.1517 216.477 11.7788C216.164 12.406 215.772 13.1898 216.281 13.3858C216.752 13.5818 216.948 13.1115 217.104 12.7979C218.084 10.7206 220.397 7.93774 223.964 7.93774Z"
        fill="#155FEE"
      />
      <path
        d="M197.853 7.93774C204.673 7.93774 208.828 12.8763 208.828 18.9123C208.828 24.9483 204.673 29.8868 197.853 29.8868C191.033 29.8868 186.879 24.9483 186.879 18.9123C186.879 12.8763 191.033 7.93774 197.853 7.93774ZM197.853 24.5563C200.949 24.5563 202.556 21.9695 202.556 18.9123C202.556 15.8551 200.949 13.2682 197.853 13.2682C194.757 13.2682 193.15 15.8551 193.15 18.9123C193.15 21.9695 194.757 24.5563 197.853 24.5563Z"
        fill="#155FEE"
      />
      <path
        d="M178.717 6.05649V1.03955H184.988V6.05649H178.717ZM178.717 29.2598V8.56495H184.988V29.2598H178.717Z"
        fill="#155FEE"
      />
      <path
        d="M175.902 13.5819H170.258V22.2047C170.258 24.1645 170.336 24.2429 172.139 24.2429H175.902V29.2598H170.101C165.946 29.2598 163.986 27.888 163.986 22.9103V13.5819H160.224V8.56495H163.986V2.9209H170.258V8.56495H175.902V13.5819Z"
        fill="#155FEE"
      />
      <path
        d="M152.689 6.05649V1.03955H158.96V6.05649H152.689ZM152.689 29.2598V8.56495H158.96V29.2598H152.689Z"
        fill="#155FEE"
      />
      <path
        d="M142.96 1.03955H149.232V29.2598H142.96V28.5151C142.96 27.9272 143.156 27.4177 143.431 26.8297C143.744 26.1634 144.097 25.4971 143.509 25.3011C143.117 25.1836 142.921 25.6147 142.804 25.8499C141.549 28.3583 139.394 29.8869 136.297 29.8869C130.614 29.8869 127.283 25.0268 127.283 18.9124C127.283 12.798 130.614 7.93784 136.297 7.93784C139.433 7.93784 141.471 9.58402 142.804 11.9357C142.96 12.2101 143.235 12.7588 143.705 12.5236C144.215 12.2885 143.705 11.583 143.392 10.9166C143.117 10.3679 142.96 9.77999 142.96 9.19207V1.03955ZM138.1 24.5564C141.079 24.5564 142.96 21.9304 142.96 18.9124C142.96 15.8944 141.079 13.2683 138.1 13.2683C134.965 13.2683 133.554 15.8944 133.554 18.9124C133.554 21.9304 134.965 24.5564 138.1 24.5564Z"
        fill="#155FEE"
      />
      <path
        d="M121.012 0.098877L116.622 6.05649H112.859L115.995 0.098877H121.012ZM125.715 18.2853C125.715 18.8732 125.676 19.8923 125.598 20.4802H110.586C111.017 23.1062 112.781 24.87 115.407 24.87C117.367 24.87 118.582 23.8901 119.091 22.3615H125.323C124.5 26.281 121.286 29.8869 115.446 29.8869C108.234 29.8869 104.393 25.0268 104.393 18.9124C104.393 12.4844 108.391 7.93784 115.329 7.93784C120.581 7.93784 125.715 11.1518 125.715 18.2853ZM115.446 12.798C112.468 12.798 111.213 14.4834 110.743 16.7175H119.562C119.327 14.209 117.759 12.798 115.446 12.798Z"
        fill="#155FEE"
      />
      <g opacity="0.6">
        <path
          d="M6.14286 35.7922H0V54.2078H20.4545V105.351H40.9091V99.2078H38.8571V103.299H22.4935V52.1559H2.03896V37.8442H6.12987M53.1688 54.2078H61.3507V48.065H59.2987V52.1559H53.1558"
          fill="#155FEE"
        />
      </g>
      <g opacity="0.8">
        <path
          d="M47.0519 93.065H44.987L45.013 97.1559H28.6493V46.013H8.19478V31.7013H12.2857V29.6624H6.15582V48.0649H26.6104V99.2078H47.0649V93.065H47.0519ZM53.1818 48.0649H67.5065V41.9221H65.4545V46.013H53.1818V48.0649Z"
          fill="#155FEE"
        />
      </g>
      <path d="M73.6363 41.9221V23.5195H12.2727V41.9221H32.7272V93.065H53.1818V41.9221H73.6363Z" fill="#155FEE" />
    </svg>
  );

  return (
    <LogoRoot
      ref={ref}
      component={RouterLink}
      href={href}
      aria-label="Logo"
      underline="none"
      className={mergeClasses([logoClasses.root, className])}
      sx={[
        () => ({
          width: 40,
          height: 40,
          ...(!isSingle && {width: 102, height: 36}),
          ...(disabled && {pointerEvents: 'none'}),
        }),
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
      {...other}
    >
      {isSingle ? singleLogo : fullLogo}
    </LogoRoot>
  );
});

Logo.displayName = 'Logo';

// ----------------------------------------------------------------------

const LogoRoot = styled(Link)(() => ({
  flexShrink: 0,
  color: 'transparent',
  display: 'inline-flex',
  verticalAlign: 'middle',
}));
